import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { NumberField } from '@/components/Field';
import { ParaQuien, type RenglonParaQuien } from '@/components/ParaQuien';
import { AvisoDesajuste } from '@/components/AvisoDesajuste';
import {
  verAgenda,
  verTarea,
  completarProduccion,
  revertirProduccion,
  type TareaProduccion,
} from '@/db/repos/production';
import { pedidosPorRecetaEntre, type PedidoDeReceta } from '@/db/repos/orders';
import { descartarPlatillo } from '@/db/repos/menu';
import {
  hoy,
  sumarDias,
  formatearMedio,
  formatearCorto,
  esHoy,
  type FechaLocal,
} from '@/domain/dates';
import { formatearCentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';

type Vista = { modo: 'agenda' } | { modo: 'cocinar'; plannedItemId: string };

/**
 * La pantalla de la manana: que se entrega hoy y que hay que cocinar hoy.
 *
 * Son dos listas distintas a proposito. Un platillo con un dia de anticipacion
 * aparece hoy en "cocinar" y manana en "entregar", nunca en las dos al mismo
 * tiempo. Confundirlas es cocinar el dia equivocado.
 */
export function ProductionPage() {
  const [fecha, setFecha] = useState<FechaLocal>(hoy());
  const [vista, setVista] = useState<Vista>({ modo: 'agenda' });
  const [error, setError] = useState<string | null>(null);

  const agenda = useLiveQuery(() => verAgenda(fecha), [fecha]);

  /**
   * Lo que de verdad esta pedido. La ventana es amplia porque la agenda mezcla
   * dias: lo atrasado se vende despues, y lo de hoy se cocina para manana.
   */
  const pedidos = useLiveQuery(
    () => pedidosPorRecetaEntre(sumarDias(fecha, -21), sumarDias(fecha, 21)),
    [fecha],
  );
  const pedidoDe = (tarea: TareaProduccion): PedidoDeReceta | undefined =>
    pedidos?.get(`${tarea.platillo.fechaVenta}|${tarea.platillo.recipeId}`);

  /** La tarea vista como renglon del bloque "para quién". */
  const paraQuien = (tarea: TareaProduccion): RenglonParaQuien => ({
    nombreReceta: tarea.nombreReceta,
    estimado: tarea.platillo.porcionesPlaneadas,
    pedido: pedidoDe(tarea),
  });

  /** Igual, pero partiendo del id del platillo: lo que tiene el formulario. */
  const pedidoDelPlatillo = (plannedItemId: string): PedidoDeReceta | undefined => {
    const tarea = [
      ...(agenda?.atrasados ?? []),
      ...(agenda?.prepararHoy ?? []),
      ...(agenda?.entregasDeHoy ?? []),
    ].find((t) => t.platillo.id === plannedItemId);
    return tarea === undefined ? undefined : pedidoDe(tarea);
  };

  if (vista.modo === 'cocinar') {
    return (
      <CocinarForm
        plannedItemId={vista.plannedItemId}
        fecha={fecha}
        pedido={pedidoDelPlatillo(vista.plannedItemId)}
        onListo={() => setVista({ modo: 'agenda' })}
        onCancelar={() => setVista({ modo: 'agenda' })}
      />
    );
  }

  const abrir = (id: string) => setVista({ modo: 'cocinar', plannedItemId: id });

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Producción"
        descripcion={esHoy(fecha) ? 'Qué cocinar hoy y qué entregar' : formatearMedio(fecha)}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      <div className="flex items-center gap-2">
        <Button variante="secundario" completo={false} onClick={() => setFecha(sumarDias(fecha, -1))}>
          ‹
        </Button>
        <p className="flex-1 text-center text-sm font-medium capitalize text-texto">
          {esHoy(fecha) ? 'Hoy' : formatearCorto(fecha)}
        </p>
        <Button variante="secundario" completo={false} onClick={() => setFecha(sumarDias(fecha, 1))}>
          ›
        </Button>
      </div>

      {!esHoy(fecha) && (
        <Button variante="fantasma" onClick={() => setFecha(hoy())}>
          Volver a hoy
        </Button>
      )}

      {agenda === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {agenda !== undefined && (
        <>
          {agenda.atrasados.length > 0 && (
            <Seccion
              titulo="⚠ Atrasado"
              descripcion="Se debió cocinar antes y no se registró"
            >
              <ParaQuien renglones={agenda.atrasados.map(paraQuien)} titulo="Para quién es" />
              <div className="space-y-2">
                {agenda.atrasados.map((tarea) => (
                  <TarjetaTarea
                    key={tarea.platillo.id}
                    tarea={tarea}
                    tono="alerta"
                    pedido={pedidoDe(tarea)}
                    onCocinar={abrir}
                    onError={setError}
                  />
                ))}
              </div>
            </Seccion>
          )}

          <Seccion
            titulo="Cocinar hoy"
            descripcion="Para vender después"
          >
            {agenda.prepararHoy.length === 0 ? (
              <EstadoVacio
                titulo="Nada que cocinar hoy"
                descripcion="No hay platillos cuya preparación caiga en este día."
              />
            ) : (
              <>
                <ParaQuien
                  titulo="Para quién cocinas"
                  renglones={agenda.prepararHoy.map(paraQuien)}
                />
                <div className="space-y-2">
                  {agenda.prepararHoy.map((tarea) => (
                    <TarjetaTarea
                      key={tarea.platillo.id}
                      tarea={tarea}
                      tono="marca"
                      pedido={pedidoDe(tarea)}
                      onCocinar={abrir}
                      onError={setError}
                    />
                  ))}
                </div>
              </>
            )}
          </Seccion>

          <Seccion titulo="Entregar hoy" descripcion="Se vende en este día">
            {agenda.entregasDeHoy.length === 0 ? (
              <EstadoVacio
                titulo="Nada que entregar hoy"
                descripcion="No hay ventas planeadas para este día."
              />
            ) : (
              <>
                <ParaQuien
                  titulo="Para quién entregas"
                  renglones={agenda.entregasDeHoy.map(paraQuien)}
                />
                <div className="space-y-2">
                  {agenda.entregasDeHoy.map((tarea) => (
                    <Card key={`e-${tarea.platillo.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-texto">{tarea.nombreReceta}</p>
                          <p className="text-sm text-texto-suave">
                            {tarea.platillo.porcionesPlaneadas}{' '}
                            {tarea.platillo.porcionesPlaneadas === 1 ? 'porción' : 'porciones'} ·{' '}
                            {formatearCentavos(
                              tarea.platillo.porcionesPlaneadas *
                                tarea.platillo.precioVentaCentavos,
                            )}
                          </p>
                          {tarea.platillo.notas !== null && (
                            <p className="mt-1 text-xs italic text-texto-suave">
                              {tarea.platillo.notas}
                            </p>
                          )}
                        </div>
                        <span
                          className={`shrink-0 text-xs font-medium ${
                            tarea.yaCompletada ? 'text-exito' : 'text-aviso'
                          }`}
                        >
                          {tarea.yaCompletada ? '✓ cocinado' : 'sin cocinar'}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </Seccion>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------



/**
 * Cuantas porciones estan PEDIDAS de verdad, frente a las planeadas.
 *
 * Las porciones del menu son una intencion, no pedidos. Decir "cocina 4 de
 * enchiladas" cuando solo hay 2 pedidas se lee como si hubiera 4 personas
 * esperando, y se cocina de mas. Al reves, si ya hay 5 pedidas y el menu dice 4,
 * conviene verlo antes de prender la estufa.
 */
function PedidasDeVerdad({
  estimado,
  pedido,
}: {
  estimado: number;
  pedido: PedidoDeReceta | undefined;
}) {
  if (pedido === undefined || pedido.cuantos === 0) {
    return (
      <p className="text-sm font-medium text-texto-tenue">
        Nadie ha pedido todavía · el menú estima {estimado}
      </p>
    );
  }

  return (
    <div>
      <p className="text-sm font-semibold text-marca-fuerte">
        {pedido.porciones} {pedido.porciones === 1 ? 'porción pedida' : 'porciones pedidas'} por{' '}
        {pedido.cuantos} {pedido.cuantos === 1 ? 'persona' : 'personas'}
      </p>
      <p className="text-sm text-texto-suave">
        {pedido.clientes.map((c) => `${c.clienteNombre} ${c.porciones}`).join(', ')}
      </p>
    </div>
  );
}

interface PropsTarjeta {
  tarea: TareaProduccion;
  tono: 'marca' | 'alerta';
  /** Lo que de verdad esta pedido de ese platillo ese dia, si hay algo. */
  pedido: PedidoDeReceta | undefined;
  onCocinar: (plannedItemId: string) => void;
  onError: (mensaje: string | null) => void;
}

function TarjetaTarea({ tarea, tono, pedido, onCocinar, onError }: PropsTarjeta) {
  const [ocupado, setOcupado] = useState(false);

  async function noSeCocino() {
    setOcupado(true);
    onError(null);
    try {
      await descartarPlatillo(tarea.platillo.id, 'No se cocinó: no hubo pedidos.');
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Card className={tono === 'alerta' ? 'border-alerta bg-alerta-suave' : ''}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-texto">{tarea.nombreReceta}</p>
          {/* Manda lo pedido: aqui solo se cocina lo que la gente pidio. */}
          <PedidasDeVerdad estimado={tarea.platillo.porcionesPlaneadas} pedido={pedido} />
          <AvisoDesajuste
            cocinadas={tarea.yaCompletada ? (tarea.produccion?.porcionesAProducir ?? null) : null}
            pedido={pedido}
          />
          <p className="mt-0.5 text-xs text-texto-tenue">
            Se vende el {formatearMedio(tarea.platillo.fechaVenta)} · costo{' '}
            {formatearCentavos(tarea.costoTotalCentavos)}
            {tarea.costoIncompleto && ' (incompleto)'}
          </p>
          {tarea.platillo.notas !== null && (
            <p className="mt-1 text-xs italic text-texto-suave">{tarea.platillo.notas}</p>
          )}
        </div>
      </div>

      {tarea.faltaInventario && (
        <p className="mt-2 text-xs font-medium text-alerta">
          ⚠ No alcanza el inventario de{' '}
          {tarea.ingredientes
            .filter((i) => i.insuficiente)
            .map((i) => i.nombreInsumo)
            .join(', ')}
        </p>
      )}

      <div className="mt-3 space-y-2">
        <Button onClick={() => onCocinar(tarea.platillo.id)}>Ver y cocinar</Button>

        {/*
          La salida al aviso, puesta DONDE APARECE EL AVISO. La accion vivia solo
          en Menu, y un platillo atrasado es de dias atras: habia que navegar
          semanas hacia atras para apagar una alerta que estorba aqui.
        */}
        {tono === 'alerta' && (
          <Button
            variante="secundario"
            disabled={ocupado}
            onClick={() => void noSeCocino()}
          >
            No lo cociné, no hubo pedidos
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PropsCocinar {
  plannedItemId: string;
  fecha: FechaLocal;
  /** Lo que la gente pidio. Es lo que manda para decidir cuanto cocinar. */
  pedido: PedidoDeReceta | undefined;
  onListo: () => void;
  onCancelar: () => void;
}

/**
 * Detalle de la tarea de cocina: los ingredientes escalados, si alcanza el
 * inventario, y el boton que descuenta y congela el costo.
 */
function CocinarForm({ plannedItemId, fecha, pedido, onListo, onCancelar }: PropsCocinar) {
  const [porciones, setPorciones] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advertencias, setAdvertencias] = useState<string[]>([]);

  /**
   * Cuantas porciones se van a hacer si no se toca el campo.
   *
   * MANDAN LOS PEDIDOS, no el numero del menu. Aqui solo se cocina lo que la
   * gente pidio: el numero del menu es un estimado para poder escalar la receta,
   * no una cantidad que haya que producir. Proponer el estimado llevaba a cocinar
   * de mas.
   *
   * Sin pedidos anotados no hay nada que proponer, y entonces si se cae al numero
   * del menu.
   */
  const pedidas = pedido !== undefined && pedido.porciones > 0 ? pedido.porciones : null;

  const escrito = porciones === null ? undefined : Number(porciones);
  const porcionesValidas = escrito === undefined || (Number.isFinite(escrito) && escrito > 0);
  const porcionesNumero = escrito ?? pedidas ?? undefined;

  const tarea = useLiveQuery(
    () =>
      verTarea(
        plannedItemId,
        porcionesValidas && porcionesNumero !== undefined ? porcionesNumero : undefined,
      ).then((t) => t ?? null),
    [plannedItemId, porcionesNumero, porcionesValidas],
  );

  if (tarea === undefined) {
    return <p className="text-sm text-texto-suave">Cargando…</p>;
  }

  if (tarea === null) {
    return (
      <div className="space-y-5">
        <PageHeader titulo="No se encontró el platillo" />
        <Button variante="secundario" onClick={onCancelar}>
          Volver
        </Button>
      </div>
    );
  }

  async function cocinar() {
    setGuardando(true);
    setError(null);
    try {
      const resultado = await completarProduccion(plannedItemId, {
        fecha,
        ...(porcionesValidas && porcionesNumero !== undefined
          ? { porciones: porcionesNumero }
          : {}),
      });

      if (resultado.advertencias.length > 0) {
        setAdvertencias(resultado.advertencias);
        setGuardando(false);
        return;
      }
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function revertir() {
    // La guarda se repite aqui porque las funciones declaradas no heredan el
    // estrechamiento de tipo del cuerpo del componente.
    if (tarea === undefined || tarea === null || tarea.produccion === null) return;
    setGuardando(true);
    setError(null);
    try {
      await revertirProduccion(tarea.produccion.id);
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        titulo={tarea.nombreReceta}
        descripcion={`Se vende el ${formatearMedio(tarea.platillo.fechaVenta)}`}
      />

      {tarea.yaCompletada && (
        <Banner tono="exito" titulo="Ya se cocinó">
          Se cocinaron {tarea.produccion?.porcionesAProducir ?? 0} porciones. El inventario ya se
          descontó y el costo quedó registrado en{' '}
          {formatearCentavos(tarea.produccion?.costoTotalCentavos ?? 0)}.
        </Banner>
      )}

      {tarea.yaCompletada && (
        <AvisoDesajuste
          cocinadas={tarea.produccion?.porcionesAProducir ?? null}
          pedido={pedido}
        />
      )}

      {!tarea.yaCompletada && (
        <>
          {pedido !== undefined && pedido.cuantos > 0 && (
            <Card className="bg-superficie-suave">
              <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
                Te pidieron
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-marca-fuerte">
                {pedido.porciones} {pedido.porciones === 1 ? 'porción' : 'porciones'}
              </p>
              <ul className="mt-1 space-y-0.5">
                {pedido.clientes.map((c, i) => (
                  <li key={`${c.clienteNombre}-${i}`} className="text-sm text-texto-suave">
                    · {c.clienteNombre} — {c.porciones}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <NumberField
            etiqueta="Cuántas porciones vas a hacer"
            valor={porciones ?? String(porcionesNumero ?? tarea.platillo.porcionesPlaneadas)}
            onCambio={setPorciones}
            decimal={false}
            error={porcionesValidas ? null : 'Pon un número mayor que cero'}
            ayuda={
              pedidas !== null
                ? `Son las ${pedidas} que te pidieron. Cámbialo si vas a hacer otra cantidad.`
                : 'Nadie ha pedido todavía. El número es solo el estimado del menú.'
            }
          />
        </>
      )}

      <Card className="bg-superficie-suave">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-texto-suave">Costo de los ingredientes</span>
          <span className="text-xl font-bold tabular-nums text-texto">
            {formatearCentavos(tarea.costoTotalCentavos)}
          </span>
        </div>
        {tarea.costoIncompleto && (
          <p className="mt-1 text-xs text-alerta">
            Incompleto: falta el precio de {tarea.nombresSinCosto.join(', ')}
          </p>
        )}
      </Card>

      {tarea.faltaInventario && !tarea.yaCompletada && (
        <Banner tono="alerta" titulo="No alcanza el inventario">
          Puedes cocinar de todos modos y el inventario quedará en negativo, que es una señal de
          que falta registrar una compra. No te va a bloquear.
        </Banner>
      )}

      <Seccion titulo="Ingredientes" descripcion={`Para ${tarea.porcionesAProducir} porciones`}>
        {tarea.ingredientes.length === 0 ? (
          <EstadoVacio
            titulo="Esta receta no tiene ingredientes"
            descripcion="Agrégalos en la receta para poder registrar la producción."
          />
        ) : (
          <ul className="space-y-2">
            {tarea.ingredientes.map((ing) => (
              <li key={ing.ingredientId}>
                <Card className={ing.insuficiente ? 'border-alerta' : ''}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-texto">{ing.nombreInsumo}</p>
                      <p className="text-sm text-texto-suave">
                        Necesitas <strong>{ing.cantidadLegible}</strong> · tienes{' '}
                        {ing.disponibleLegible}
                      </p>
                      {ing.insuficiente && (
                        <p className="mt-0.5 text-xs font-medium text-alerta">
                          Faltan {ing.faltanteLegible}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-sm tabular-nums text-texto-suave">
                      {ing.costoCentavos === null
                        ? 'sin costo'
                        : formatearCentavos(ing.costoCentavos)}
                    </span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      {advertencias.length > 0 && (
        <Banner tono="aviso" titulo="Se registró con avisos">
          <ul className="list-disc space-y-1 pl-4">
            {advertencias.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
          <div className="mt-3">
            <Button variante="secundario" onClick={onListo}>
              Entendido
            </Button>
          </div>
        </Banner>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo registrar">
          {error}
        </Banner>
      )}

      {advertencias.length === 0 && (
        <div className="space-y-2">
          {!tarea.yaCompletada && (
            <Button
              tamano="grande"
              disabled={guardando || !porcionesValidas || tarea.ingredientes.length === 0}
              onClick={() => void cocinar()}
            >
              {guardando ? 'Registrando…' : 'Ya lo cociné, descontar del inventario'}
            </Button>
          )}
          <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
            {tarea.yaCompletada ? 'Volver' : 'Cancelar'}
          </Button>
          {tarea.yaCompletada && tarea.produccion !== null && (
            <Button variante="fantasma" disabled={guardando} onClick={() => void revertir()}>
              Deshacer esta producción
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
